import React, { useState, useMemo } from 'react';
import { Box, AppBar, Toolbar, Typography, Button, IconButton, useMediaQuery, useTheme, alpha, Avatar } from '@mui/material';
import { Menu as MenuIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SideNav from './SideNav';

const AdminLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Only show SideNav on specific routes, excluding home and dashboard
  const showSideNav = ![
    '/', 
    '/admin', 
    '/admin/dashboard'
  ].includes(location.pathname);
  
  // Determine the current section title based on the URL path
  const sectionTitle = useMemo(() => {
    if (location.pathname.includes('/admin/regions')) {
      return 'Wine Regions';
    } else if (location.pathname.includes('/admin/users')) {
      return 'Admin Users';
    } else if (location.pathname.includes('/admin/grapes')) {
      return 'Grape Varieties';
    } else if (location.pathname.includes('/admin/producers')) {
      return 'Wine Producers';
    } else if (location.pathname.includes('/admin/wine-names')) {
      return 'Wine Names';
    } else if (location.pathname.includes('/admin/wines')) {
      return 'Wines';
    } else if (location.pathname.includes('/admin/venues')) {
      return 'Venues';
    } else if (location.pathname.includes('/admin/suppliers')) {
      return 'Suppliers';
    } else if (location.pathname.includes('/admin/dashboard') || location.pathname === '/admin') {
      return 'Dashboard';
    }
    return '';
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const getUserInitials = (user: any) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
    } else if (user?.firstName) {
      return user.firstName.charAt(0);
    }
    return 'A';
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100vh', 
      backgroundColor: theme.palette.background.default 
    }}>
      {/* Sidebar - only shown on specific routes */}
      {showSideNav && (
        <Box 
          sx={{ 
            width: { md: 280 }, 
            flexShrink: 0,
          }}
        >
          <SideNav
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? mobileOpen : true}
            onClose={isMobile ? handleDrawerToggle : undefined}
          />
        </Box>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, pt: 2 }}>
          <Box 
            component="header"
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Toolbar sx={{ px: 3, minHeight: '64px !important' }}>
            {showSideNav && isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 2,
                  color: theme.palette.text.primary 
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}>
              <Box>
                {(location.pathname.includes('/dashboard') || location.pathname === '/admin') ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                    <img 
                      src="/assets/logo-nota-wine.svg" 
                      alt="Nota Wine" 
                      style={{ 
                        height: '42px',
                        width: 'auto',
                      }}
                    />
                  </Box>
                ) : (
                  <Typography 
                    variant="h5" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {sectionTitle}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {!(location.pathname.includes('/dashboard') || location.pathname === '/admin') && (
                  <Box sx={{ 
                    display: { xs: 'none', sm: 'flex' }, 
                    alignItems: 'center', 
                    gap: 1.5,
                  }}>
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: theme.palette.primary.main,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {getUserInitials(user)}
                    </Avatar>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                      }}
                    >
                      {user?.firstName || 'Admin'}
                    </Typography>
                  </Box>
                )}
                
                <Button 
                  variant="outlined"
                  startIcon={<LogoutIcon />}
                  onClick={() => logout()}
                  sx={{ 
                    minWidth: 'auto',
                    px: 2,
                    py: 0.75,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  Logout
                </Button>
              </Box>
            </Box>
            </Toolbar>
          </Box>
        </Box>
        
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
          backgroundColor: theme.palette.background.default,
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
